import { ReactNode } from "react";
import { BaseProps } from "../../../BaseProps";
import { getBasePropsFromHtmlProps } from "../../../utilities/getBasePropsFromHtmlProps";
import { getHtmlPropsFromBaseProps } from "../../../utilities/getHtmlPropsFromBaseProps";
import { useValidity } from "../../ValidityGroup";
import { Body } from "../../../typography";
import { createComponentStyleHook } from "../../../tokens";
import { getStyleModeSafeProps } from "../../utils/getStyleModeSafeProps";

import tokenStyles from "./styles.module.css";
import legacyStyles from "./styles.module.scss";

/**
 * The `CharacterCount` component properties.
 */
export interface CharacterCountProps extends BaseProps {

  /**
  * A ReactNode passed to be wrapped by CharacterCount.
  */
  readonly children?: ReactNode

  /**
  * Count of characters typed.
  */
  readonly characterCount: number;

  /**
  * Maximum length of characters.
  */
  readonly maxLength?: number;
}

const useResolvedCharacterCountStyles = createComponentStyleHook<CharacterCountProps>({
  legacyStyles,
  tokenStyles,
  componentTokens: "form_character_count",
});

/**
 * An input component character count block.
 */
export const CharacterCount = (props: CharacterCountProps) => {
  const { characterCount, maxLength, children } = props;
  const [invalid] = useValidity();
  const { mode, styles, StyleModeBoundary } = useResolvedCharacterCountStyles(getStyleModeSafeProps(props));

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.CharacterCount, "character-count", { invalid }],
    testIdPrefix: "character-count",
  });

  if (!maxLength) return children;

  const characterLengthMessage = `${characterCount}/${maxLength} characters`;

  return (
    <StyleModeBoundary mode={mode}>
      <Body {...getBasePropsFromHtmlProps(baseHtmlProps)} inline size="mini" >
        {characterLengthMessage}
        {children
          && (<>. {children}&nbsp;</>)
        }
      </Body>
    </StyleModeBoundary>
  );
};
